// src/components/Analytics/AnalyticsTracker.js
// VERSION AVEC HASH ET DEBOUNCE STRICT

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AnalyticsService } from '../../apiService';

// Cache global pour éviter les re-tracks
const TRACKED_PAGES = new Set();
let ACTIVE_TIMER = null;

const AnalyticsTracker = () => {
  const location = useLocation();
  const isTrackingRef = useRef(false);

  /**
   * Générer un ID unique
   */
  const generateId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  /**
   * Créer un hash unique pour cette page + session
   */
  const getPageHash = (pathname, search) => {
    const sessionId = sessionStorage.getItem('analytics_session_id') || 'no-session';
    return `${sessionId}_${pathname}${search}`;
  };

  /**
   * Obtenir ou créer Visitor ID
   */
  const getVisitorId = () => {
    let visitorId = localStorage.getItem('analytics_visitor_id');
    if (!visitorId) {
      visitorId = generateId();
      localStorage.setItem('analytics_visitor_id', visitorId);
    }
    return visitorId;
  };

  /**
   * Obtenir ou créer Session ID
   */
  const getSessionId = () => {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = generateId();
      sessionStorage.setItem('analytics_session_id', sessionId);
      sessionStorage.setItem('analytics_session_start', Date.now().toString());
    }
    return sessionId;
  };

  /**
   * Fonction de tracking ULTRA-PROTÉGÉE
   */
  const trackPageView = async (pathname, search) => {
    const pageHash = getPageHash(pathname, search);
    const pageKey = pathname + search;

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🎯 trackPageView pour:', pageKey);
    console.log('   Hash:', pageHash);
    console.log('   Déjà tracké:', TRACKED_PAGES.has(pageHash));
    console.log('   En cours:', isTrackingRef.current);

    // PROTECTION 1 : Page déjà trackée dans cette session ?
    if (TRACKED_PAGES.has(pageHash)) {
      console.log('🚫 BLOQUÉ: Page déjà trackée');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
      return;
    }

    // PROTECTION 2 : Tracking déjà en cours ?
    if (isTrackingRef.current) {
      console.log('🚫 BLOQUÉ: Tracking en cours');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
      return;
    }

    try {
      isTrackingRef.current = true;
      console.log('🔒 VERROUILLÉ');

      // Marquer immédiatement comme tracké AVANT l'envoi
      TRACKED_PAGES.add(pageHash);
      console.log('✓ Page marquée comme trackée');

      const visitorId = getVisitorId();
      const sessionId = getSessionId();
      const sessionStart = parseInt(sessionStorage.getItem('analytics_session_start'));

      const articleMatch = pathname.match(/\/news\/(\d+)/);
      const articleId = articleMatch ? parseInt(articleMatch[1]) : null;

      const eventData = {
        session_id: sessionId,
        visitor_id: visitorId,
        page_url: window.location.href,
        page_title: document.title,
        article_id: articleId,
        event_type: 'page_view',
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
        session_duration: Math.floor((Date.now() - sessionStart) / 1000),
        page_view_duration: 0,
        latitude: null,
        longitude: null,
        country: null,
        country_code: null,
        city: null,
      };

      console.log('📤 Envoi analytics...');
      await AnalyticsService.track(eventData);
      console.log('✅ SUCCÈS');

    } catch (error) {
      console.error('❌ ERREUR:', error.message);
      // En cas d'erreur, retirer du cache pour permettre un retry
      TRACKED_PAGES.delete(pageHash);
    } finally {
      isTrackingRef.current = false;
      console.log('🔓 DÉVERROUILLÉ');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    }
  };

  /**
   * Effet principal avec debounce strict
   */
  useEffect(() => {
    console.log('⚡ useEffect - Path:', location.pathname);

    // Annuler tout timer existant
    if (ACTIVE_TIMER) {
      console.log('⏹️ Annulation du timer précédent');
      clearTimeout(ACTIVE_TIMER);
      ACTIVE_TIMER = null;
    }

    // Créer un nouveau timer
    ACTIVE_TIMER = setTimeout(() => {
      console.log('⏰ Timer expiré, déclenchement du track\n');
      trackPageView(location.pathname, location.search);
      ACTIVE_TIMER = null;
    }, 500);

    // Cleanup : annuler le timer si le composant se démonte ou si la route change
    return () => {
      if (ACTIVE_TIMER) {
        console.log('🧹 Cleanup - Annulation du timer');
        clearTimeout(ACTIVE_TIMER);
        ACTIVE_TIMER = null;
      }
    };
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
